"use client";
import TelInput from "@/components/Shared/TelInput/TelInput";
import { useRecaptcha } from "@/hooks/useRecaptcha";
import { emailjsSend } from "@/util/emailjsConfig";
import { recaptchaValidate } from "@/utils/recaptchaValidate";
import { TextField } from "@mui/material";
import { motion } from "framer-motion";
import React, { useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./HomeForm.scss";

interface FormData {
	name: string;
	email: string;
	phone: string;
	message: string;
}

interface FormErrors {
	name?: string;
	email?: string;
	phone?: string;
	message?: string;
}

interface FormDataProps {
	style: string;
}

const HomeForm: React.FC<FormDataProps> = ({ style }) => {
	const [formData, setFormData] = useState<FormData>({
		name: "",
		email: "",
		phone: "",
		message: "",
	});
	const [errors, setErrors] = useState<FormErrors>({});
	const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
	const [successMessage, setSuccessMessage] = useState<string>("");

	const { recaptchaRef, verifyToken, handleRecaptchaChange, reset } = useRecaptcha();

	// Validate email
	const isEmailValid = (email: string): boolean => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	};

	// Validate fields
	const validate = (fieldValues = formData) => {
		let tempErrors: FormErrors = { ...errors };

		if ("name" in fieldValues) tempErrors.name = fieldValues.name ? "" : "Name is required";

		if ("email" in fieldValues) tempErrors.email = fieldValues.email ? (isEmailValid(fieldValues.email) ? "" : "Invalid email address") : "Email is required";

		if ("phone" in fieldValues) tempErrors.phone = fieldValues.phone ? "" : "Phone number is required";

		if ("message" in fieldValues) tempErrors.message = fieldValues.message ? "" : "Message is required";

		setErrors({
			...tempErrors,
		});

		// Check if any field has error
		return Object.values(tempErrors).every((x) => x === "");
	};

	const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { name: string; value: string } }): void => {
		const { name, value } = event.target;

		setFormData({
			...formData,
			[name]: value,
		});

		if (touched[name]) {
			validate({ ...formData, [name]: value });
		}
	};

	const handleBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { name: string } }) => {
		const { name } = event.target;
		setTouched({ ...touched, [name]: true });
		validate(formData);
	};

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
		event.preventDefault();

		if (validate()) {
			// Verify reCAPTCHA server-side
			const isRecaptchaValid = await recaptchaValidate(verifyToken);
			if (!isRecaptchaValid) return;

			// Show success toast
			const res = await emailjsSend(formData, "template_8dy15p7");
			if (res) {
				toast.success("Thank you! Your message has been sent successfully.", {
					position: "top-right",
					autoClose: 3000,
					hideProgressBar: true,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					theme: "light",
				});

				setSuccessMessage("Request Submitted Successfully!");
				setFormData({ name: "", email: "", phone: "", message: "" });
				setErrors({});
				setTouched({});
				reset();
			}

			// Reset form
		} else {
			toast.error("an error occured.", {
				position: "top-right",
				autoClose: 3000,
			});
		}
	};

	const isFormValid = () => {
		return formData.name && isEmailValid(formData.email) && formData.phone && formData.message && Object.values(errors).every((x) => x === "");
	};

	return (
		<div className={`home-form-wrapper ${style ? style : ""}`}>
			<ToastContainer />

			<div className='content'>
				<p className='section-main-header'>Contact Us</p>
				<p className='text-16 grey-600 weight-500'>For questions about orders and shipping, please complete the form below or call (519) 713-9318</p>

				<motion.div
					initial={{ opacity: 0 }}
					whileInView={{ opacity: 1, x: 0, y: 0 }}
					viewport={{ once: true, amount: 0.3 }}
					transition={{ ease: "easeOut", duration: 0.6, delay: 0 }}
					className='right'
				>
					<form className='form-container' onSubmit={handleSubmit} noValidate>
						<div className='first-row'>
							<TextField
								label='Name'
								variant='outlined'
								fullWidth
								name='name'
								value={formData.name}
								onChange={handleChange}
								onBlur={handleBlur}
								error={Boolean(errors.name)}
								helperText={errors.name}
							/>
							<TextField
								label='Email'
								variant='outlined'
								type='email'
								name='email'
								fullWidth
								value={formData.email}
								onChange={handleChange}
								onBlur={handleBlur}
								error={Boolean(errors.email)}
								helperText={errors.email}
							/>
						</div>

						<TelInput
							data={formData.phone}
							handleChange={handleChange}
							handleBlur={handleBlur}
							input={{
								name: "phone",
								label: "Phone Number",
								placeholder: "Phone Number",
								fullWidth: true,
								error: Boolean(errors.phone),
								helperText: errors.phone,
							}}
						/>

						<TextField
							label='Message'
							placeholder='How can we help you?'
							variant='outlined'
							multiline
							rows={4}
							fullWidth
							name='message'
							value={formData.message}
							onChange={handleChange}
							onBlur={handleBlur}
							error={Boolean(errors.message)}
							helperText={errors.message}
						/>

						<ReCAPTCHA ref={recaptchaRef} sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ""} onChange={handleRecaptchaChange} className='recaptcha-wrapper' />

						<button type='submit' className={`main-btn blue ${!isFormValid() && "disabled"}`} disabled={!isFormValid()}>
							Submit
						</button>
					</form>

					{successMessage && <p className='success-message'>{successMessage}</p>}
				</motion.div>
			</div>
		</div>
	);
};
export default HomeForm;
